import { forwardRef } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/cn';

/**
 * Modal — Radix Dialog wrapper with Outflanq tokens.
 *
 * Compose like Radix:
 *   <Modal>
 *     <ModalContent>
 *       <ModalHeader>
 *         <ModalTitle>...</ModalTitle>
 *         <ModalDescription>...</ModalDescription>
 *       </ModalHeader>
 *       <ModalBody>...</ModalBody>
 *       <ModalFooter>...</ModalFooter>
 *     </ModalContent>
 *   </Modal>
 */
export const Modal = DialogPrimitive.Root;
const ModalPortal = DialogPrimitive.Portal;

const ModalOverlay = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-bg-primary/80 backdrop-blur-xs',
      'data-[state=open]:animate-in data-[state=closed]:animate-out',
      'data-[state=open]:fade-in data-[state=closed]:fade-out',
      className,
    )}
    {...props}
  />
));
ModalOverlay.displayName = 'ModalOverlay';

export interface ModalContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  /** Hide the built-in top-right close button — useful for confirm-only modals. */
  hideClose?: boolean;
  /** Extra classes for the backdrop (e.g. a darker dim); default unchanged. */
  overlayClassName?: string;
}

export const ModalContent = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  ModalContentProps
>(({ className, children, hideClose = false, overlayClassName, ...props }, ref) => {
  const { t } = useTranslation();
  return (
  <ModalPortal>
    <ModalOverlay className={overlayClassName} />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
        'w-[min(92vw,560px)] max-h-[90vh] overflow-auto',
        'bg-bg-secondary border border-border rounded-md shadow-2xl',
        'p-xl',
        'focus:outline-hidden',
        className,
      )}
      {...props}
    >
      {children}
      {hideClose ? null : (
        <DialogPrimitive.Close
          className={cn(
            'absolute right-md top-md rounded-sm p-xs',
            'text-text-muted hover:text-text-primary',
            'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent',
          )}
          aria-label={t('common.close')}
        >
          <X className="h-4 w-4" />
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </ModalPortal>
  );
});
ModalContent.displayName = 'ModalContent';

export function ModalHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('flex flex-col gap-xs mb-lg', className)} {...props} />;
}

export const ModalTitle = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-h3 font-semibold text-text-primary', className)}
    {...props}
  />
));
ModalTitle.displayName = 'ModalTitle';

export const ModalDescription = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-body-sm text-text-secondary', className)}
    {...props}
  />
));
ModalDescription.displayName = 'ModalDescription';

export function ModalBody({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('flex flex-col gap-md', className)} {...props}>
      {children}
    </div>
  );
}

export function ModalFooter({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('flex justify-end gap-sm mt-xl pt-lg border-t border-border-subtle', className)}
      {...props}
    />
  );
}
